import React, { useCallback, useEffect, useRef } from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import { isNil } from 'lodash'
import PropTypes from 'prop-types'

import { TreeSelect } from '../../../common/components/sections'
import { HOTKEYS_ENABLED_TAGS } from '../../../common/constants'
import { treeOptionPropType } from '../../../common/prop-types'

import { LOCATIONS_AVAILABLE_HOTKEYS } from '../../constants'
import {
  useLocationEditableCellHotkey,
  useUpdateLocationMutation,
} from '../../hooks'

const LocationsTableTreeSelect = ({
  multiple,
  locationId,
  fieldName,
  placeholder,
  value,
  treeData,
  isDisabled,
}) => {
  const selectRef = useRef(null)
  const { isSelectInHotkeyEditMode, setHotkeyEditModeEnabled } =
    useLocationEditableCellHotkey(locationId, fieldName)
  const [updateLocation] = useUpdateLocationMutation(locationId)

  useEffect(() => {
    if (isSelectInHotkeyEditMode && !isNil(selectRef.current)) {
      selectRef.current.focus()
    }
  }, [isSelectInHotkeyEditMode])

  const handleChange = useCallback(
    newValue => {
      updateLocation({
        [fieldName]: newValue,
      })
      selectRef.current.blur()
      setHotkeyEditModeEnabled(false)
    },
    [fieldName, setHotkeyEditModeEnabled, updateLocation],
  )

  const handleBlur = useCallback(() => {
    setHotkeyEditModeEnabled(false)
  }, [setHotkeyEditModeEnabled])

  useHotkeys(
    LOCATIONS_AVAILABLE_HOTKEYS.ESC.hotkey,
    () => {
      selectRef.current.blur()
    },
    {
      enableOnTags: HOTKEYS_ENABLED_TAGS,
    },
    [isSelectInHotkeyEditMode],
  )

  return (
    <TreeSelect
      multiple={multiple}
      ref={selectRef}
      showSearch
      treeData={treeData}
      placeholder={placeholder}
      value={value}
      onChange={handleChange}
      onBlur={handleBlur}
      disabled={isDisabled}
    />
  )
}

LocationsTableTreeSelect.propTypes = {
  locationId: PropTypes.string.isRequired,
  fieldName: PropTypes.string.isRequired,
  placeholder: PropTypes.string.isRequired,
  isDisabled: PropTypes.bool,
  value: PropTypes.string,
  treeData: PropTypes.arrayOf(treeOptionPropType),
  multiple: PropTypes.bool,
}

LocationsTableTreeSelect.defaultProps = {
  value: undefined,
  isDisabled: false,
  treeData: [],
  multiple: false,
}

export default LocationsTableTreeSelect
